import React, {useState, useEffect, useRef} from 'react'
import {withRouter} from "react-router-dom";
import {loadStripe} from "@stripe/stripe-js";
import axios from "axios";
import {handleError} from "../common/utils";
import CustomerLoading from "../components/CustomerLoading";
import AddressAndRatesForm from "../components/AddressAndRatesForm";
import StripeInvoiceSummary from "../components/StripeInvoiceSummary";
import agent from "../api/agent";
import {Elements} from "@stripe/react-stripe-js";
import StripePayment from "../components/StripePayment";
import ButtonLoading from "../components/ButtonLoading";
import {PageHeader, Skeleton} from 'antd'
import {restrictionStatus, warehouseData} from "../common/constants";
import GlobalError from '../components/GlobalError';

const googleAddressKey = 'AIzaSyDe7SUUVtVJJ5DHYe-ZuKkBV3Wh-fbV7PY'
const loader = 'auto';

const StripeCheckout = (props) => {
  const {packageId} = props.match.params
  const [isLoading, setIsLoading] = useState(false)
  const [clientSecret, setClientSecret] = useState('')
  const [paymentId, setPaymentId] = useState('')
  const [customerOptions, setCustomerOptions] = useState({customer: '', ephemeralKey: ''})
  const [invoiceData, setInvoiceData] = useState({})
  const [packageData, setPackageData] = useState({})
  const [invoiceId, setInvoiceId] = useState('')
  const [addresses, setAddresses] = useState([])
  const [invoiceUpdate, setInvoiceUpdate] = useState({})
  const [allowCheckout, setAllowCheckout] = useState(false)
  const [hasCheckoutThrownError, setHasCheckoutThrownError] = useState(false)
  const [shippingAddress, setShippingAddress] = useState({})
  const [noPaymentLoading, setNoPaymentLoading] = useState(false)
  const [paypalClientId, setPaypalClientId] = useState('')
  const [stripePromise, setStripePromise] = useState(null)
  const [rightSideLoading, setRightSideLoading] = useState(false)
  const [warehouse, setWarehouse] = useState('')
  const [usageBilling, setUsageBilling] = useState(false)
  const [usageLoading, setUsageLoading] = useState(false)
  const usageLock = useRef(false)

  useEffect(() => {
    document.body.style.paddingTop = '0';
  }, []);

  const getStripePromise = (stripePublishableKey) => {
    const promise = loadStripe(stripePublishableKey, {apiVersion: '2024-06-20',betas: ['elements_customers_beta_1']})
    setStripePromise(promise)
  }

  const getAddresses = async (warehouse) => {
    try {
      setIsLoading(true)
      setAddresses([])

      const requestBody = {
        packageId: packageId
      }

      const response = await axios.post('/v2/customer/addresses/getAddresses', requestBody)

      const customerAddresses = response.data.addresses
      const pickupAddresses = {
        addressId: Number(-warehouse),
        name: `Pickup in ${warehouseData(warehouse).name}`,
        phone: warehouseData(warehouse).phone,
        street1: warehouseData(warehouse).street1,
        street2: warehouseData(warehouse).street2,
        city: warehouseData(warehouse).city,
        stateCode: warehouseData(warehouse).stateCode,
        countryCode: warehouseData(warehouse).countryCode,
        postalCode: warehouseData(warehouse).postalCode,
      }

      customerAddresses.push(pickupAddresses)
      setAddresses(customerAddresses)

    } catch (e) {
      handleError(e)
    } finally {
      setIsLoading(false)
    }
  }

  const createServicesInvoice = async () => {
    try {
      setIsLoading(true)
      const postData = {
        packageId: packageId,
      }
      const response = await axios.post('/v1/checkout/services', postData)
      const stripePublishableKey = response.data.data.credentials.stripePublishableKey;
      await getStripePromise(stripePublishableKey)
      
      setUsageBilling(!!response.data.data.usageBillingEnabled)
      setInvoiceData(response.data.data)
      setInvoiceId(response.data.data.invoiceId)
      setPackageData(response.data.data.package)
      setPaypalClientId(response.data.data.credentials.paypalClientId)
    } catch (e) {
      handleError(e)
    } finally {
      setIsLoading(false)
    }
  }

  const processStripeCheckout = async (invoiceId) => {
    try {
      const requestBody = {
        invoiceId: invoiceId
      }

      const response = await agent.checkout.stripeCheckout(requestBody)
      const stripeRes = response.data.data
      setClientSecret(stripeRes.paymentIntent)
      setPaymentId(stripeRes.paymentIntentId)
      setCustomerOptions({customer: stripeRes.stripeCustomerId, ephemeralKey: stripeRes.ephemeralKey})
    } catch (e) {
      handleError(e)
    }
  }

  const usageCheckout = async () => {
    if (usageLock.current) {
      return
    }

    usageLock.current = true

    try {
      setUsageLoading(true)
      await agent.checkout.usageCheckout({
        invoiceId: invoiceData.invoiceId,
        packageId: packageId,
      })
      props.history.replace('/done/packageShipping')
    } catch (e) {
      handleError(e)
    } finally {
      usageLock.current = false
      setUsageLoading(false)
    }
  }

  const noPaymentCheckout = async () => {
    try {
      setNoPaymentLoading(true)
      const postData = {
        invoiceId: invoiceData.invoiceId,
        packageId: packageId,
      }

      await agent.checkout.noPaymentCheckout(postData)
      props.history.replace('/done/packageShipping')

    }catch (e) {
      handleError(e)
    }finally {
      setNoPaymentLoading(false)
    }
  }

  const goBack = () => {
    props.history.push('/package?pkg=' + packageId)
  }

  const checkoutRequirement = packageData.disposition === 0 && packageData.lockedForProcessing === 0

  useEffect(() => {
    if (packageId) {
      createServicesInvoice()
    }
  }, [packageId])

  useEffect(() => {
    if (packageData.warehouse) {
      getAddresses(packageData.warehouse)
      setWarehouse(packageData.warehouse)
    }
  }, [packageData])

  useEffect(() => {
    if (invoiceId) {
      processStripeCheckout(invoiceId)
    }
  }, [invoiceId]);

  useEffect( () => {
    if (invoiceUpdate.invoiceId) {
      setInvoiceData(invoiceUpdate)
      setAllowCheckout(true)
    }
    
  }, [invoiceUpdate])

  if (isLoading || !stripePromise || !clientSecret) {
    return (
      <div>
        <CustomerLoading/>
      </div>
    )
  }

  const options = {
    clientSecret,
    loader,
    customerOptions
  }

  if(hasCheckoutThrownError) {
    return (
      <div>
        <GlobalError/>
      </div>
    )
  }

  return (
    <div  className="stripe-checkout">
      <div className={`stripe-checkout-banner${usageLoading ? ' checkout-locked' : ''}`}>
        <PageHeader title={<div>Checkout Package #{packageId?.toString().padStart(6, '0')} </div>} onBack={usageLoading ? () => {} : goBack}/>
      </div>
      <div className="stripe-checkout-container">
        <div className={`stripe-checkout-sub-container left-sub-container${usageLoading ? ' checkout-locked' : ''}`}>
          <div className="stripe-checkout-content">
          {packageData?.restrictDomesticOnly && packageData?.restricted !== 27 ? (
            <div className="notice notice-warn">
              <i>
                <svg className="feather">
                  <use href="/assets/images/icons/feather-sprite.svg#alert-triangle" />
                </svg>
              </i>
              <div className="notice-content">
                Due to export restrictions, this package can only be shipped within the same country. Your shipping addresses have been filtered accordingly.
              </div>
            </div>
          ) : null}

          {packageData?.restrictGroundOnly && packageData?.restricted !== 26 ? (
            <div className="notice notice-warn">
              <i>
                <svg className="feather">
                  <use href="/assets/images/icons/feather-sprite.svg#alert-triangle" />
                </svg>
              </i>
              <div className="notice-content">
                Due to carrier restrictions, this package can only be shipped via surface delivery methods.
              </div>
            </div>
          ) : null}

          {packageData?.restricted && restrictionStatus(packageData?.restricted) ? (
            <div className="notice notice-warn">
              <i>
                <svg className="feather">
                  <use href="/assets/images/icons/feather-sprite.svg#alert-triangle" />
                </svg>
              </i>
              <div className="notice-content">{restrictionStatus(packageData?.restricted)}</div>
            </div>
          ) : null}

          {packageData?.restrictPickupOnly ? (
            <div className="notice notice-warn">
              <i>
                <svg className="feather">
                  <use href="/assets/images/icons/feather-sprite.svg#alert-triangle" />
                </svg>
              </i>
              <div className="notice-content">
                Due to export restrictions, this package can only be picked up from Reship warehouse. Your shipping addresses have been filtered accordingly.
              </div>
            </div>
          ) : null}

            {clientSecret && <Elements stripe={stripePromise} options={{clientSecret}}>
          <AddressAndRatesForm paymentId={paymentId} setInvoiceUpdate={setInvoiceUpdate} addresses={addresses} getAddress={getAddresses} warehouse={warehouse} packageId={parseInt(packageId)} setShippingAddress={setShippingAddress} setAllowCheckout={setAllowCheckout} googleAddressKey={googleAddressKey} setHasCheckoutThrownError={setHasCheckoutThrownError} setRightSideLoading={setRightSideLoading}/>
          </Elements>}
          </div>
        </div>

        <div className="stripe-checkout-sub-container right-sub-container">
          <div className="content-sticky">
            {!!invoiceData && <div className={usageLoading ? 'checkout-locked' : ''}>
              <StripeInvoiceSummary invoiceData={invoiceData} setInvoiceUpdate={setInvoiceUpdate} paymentIntentId={paymentId} allowCheckout={allowCheckout} actionsLocked={usageLoading}/>
            </div>}
            {rightSideLoading ? <div className="right-side-skeleton"><Skeleton active={true}/></div> : <div>{allowCheckout && checkoutRequirement && invoiceData.balance >0 && <div className="stripe-checkout-content">
              {usageBilling && (usageLoading ? <div className="btn btn-outline-primary srr-button usage-invoice-button"><ButtonLoading/></div> :
              <button className="btn btn-outline-primary srr-button usage-invoice-button" onClick={usageCheckout} disabled={usageLoading}>Add to monthly invoice</button>)}
              {clientSecret && <Elements stripe={stripePromise} options={options}>
                {paypalClientId && <div className={usageLoading ? 'checkout-locked' : ''}>
                  <StripePayment shippingAddress={shippingAddress} googleAddressKey={googleAddressKey} invoiceId={invoiceData.invoiceId} invoiceAmount={invoiceData.balance} packageId={packageId} paypalClientId={paypalClientId} mobile={false} actionsLocked={usageLoading}/>
                </div>}
              </Elements>}
            </div>}
            </div>}
            {allowCheckout && checkoutRequirement && invoiceData.balance === 0 && <div className="stripe-checkout-content">{
              noPaymentLoading? <div className="btn btn-primary srr-button"><ButtonLoading/></div> :
              <button className="btn btn-primary srr-button" onClick={noPaymentCheckout}>Ship Package</button>
            }
            </div>}
          </div>
        </div>
      </div>
    </div>
  )
}


export default withRouter(StripeCheckout)